import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DoorOpen, DoorClosed, MoveHorizontal, Plus, Trash2, Columns3 } from 'lucide-react';
import { gatePostSizes } from '../data/gatePostPricing';

const GateConfiguration = ({ 
  selectedModel,
  gates,
  onGatesChange,
  gatePostSize,
  onGatePostSizeChange,
  gatePostQuantity,
  onGatePostQuantityChange
}) => {
  if (!selectedModel) return null;

  const handleGateTypeToggle = (gateType) => {
    const newGates = { ...gates };
    if (newGates[gateType].enabled) {
      newGates[gateType] = { enabled: false, items: [] };
    } else {
      newGates[gateType] = {
        enabled: true,
        items: [{ width: '', height: '', quantity: 1 }]
      };
    }
    onGatesChange(newGates);
  };

  const handleAddGateItem = (gateType) => {
    const newGates = { ...gates };
    newGates[gateType].items.push({ width: '', height: '', quantity: 1 });
    onGatesChange(newGates);
  };

  const handleRemoveGateItem = (gateType, index) => {
    const newGates = { ...gates };
    newGates[gateType].items = newGates[gateType].items.filter((_, i) => i !== index);
    if (newGates[gateType].items.length === 0) {
      newGates[gateType].enabled = false;
    }
    onGatesChange(newGates);
  };

  const handleGateItemChange = (gateType, index, field, value) => {
    const newGates = { ...gates };
    newGates[gateType].items[index][field] = value;
    onGatesChange(newGates);
  };

  const renderGateConfig = (gateType, title, icon) => {
    const isEnabled = gates[gateType].enabled;

    return (
      <div key={gateType} className="space-y-3">
        <div className="flex items-center space-x-2 p-3 rounded-lg border-2 border-gray-200 hover:border-emerald-300 transition-all">
          <Checkbox
            id={gateType}
            checked={isEnabled}
            onCheckedChange={() => handleGateTypeToggle(gateType)}
          />
          <Label htmlFor={gateType} className="flex-1 cursor-pointer flex items-center gap-2">
            {icon}
            <div className="font-medium">{title}</div>
          </Label>
        </div>

        {isEnabled && (
          <div className="ml-6 space-y-4">
            {gates[gateType].items.map((item, index) => (
              <div key={index} className="p-4 border-2 border-gray-200 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">Tor #{index + 1}</span>
                  {gates[gateType].items.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveGateItem(gateType, index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Breite (cm)</Label>
                    <Input
                      type="number"
                      min="50"
                      max="700"
                      value={item.width}
                      onChange={(e) => handleGateItemChange(gateType, index, 'width', e.target.value)}
                      placeholder="z.B. 100"
                      className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Höhe (cm)</Label>
                    <Input
                      type="number"
                      min="50"
                      max="250"
                      value={item.height}
                      onChange={(e) => handleGateItemChange(gateType, index, 'height', e.target.value)}
                      placeholder="z.B. 180"
                      className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Anzahl</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={item.quantity}
                      onChange={(e) => handleGateItemChange(gateType, index, 'quantity', e.target.value)}
                      placeholder="1"
                      className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              onClick={() => handleAddGateItem(gateType)}
              variant="outline"
              size="sm"
              className="w-full border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition-all"
            >
              <Plus className="w-4 h-4 mr-2" />
              Weiteres Tor hinzufügen
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DoorOpen className="w-5 h-5 text-emerald-600" />
          Tore konfigurieren
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Wählen Sie die gewünschten Tor-Typen für Modell <span className="font-bold">{selectedModel.name}</span>
        </p>

        {renderGateConfig('pedestrian', 'Gehtüre (Fußgängertor)', <DoorClosed className="w-4 h-4 text-gray-500" />)}
        {renderGateConfig('double', 'Doppelflügeltor', <DoorOpen className="w-4 h-4 text-gray-500" />)}
        {renderGateConfig('sliding', 'Schiebetor (freitragend)', <MoveHorizontal className="w-4 h-4 text-gray-500" />)}

        {!gates.pedestrian.enabled && !gates.double.enabled && !gates.sliding.enabled && (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-sm text-gray-500">Keine Tore ausgewählt</p>
          </div>
        )}

        {/* Gate Posts Selection */}
        {(gates.pedestrian.enabled || gates.double.enabled || gates.sliding.enabled) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-2 border-cyan-200">
            <div className="flex items-center gap-2 mb-3">
              <Columns3 className="w-5 h-5 text-cyan-600" />
              <Label className="text-base font-semibold text-gray-900">Torsteher</Label>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Wählen Sie die Stehergröße für Ihre Tore (4mm Wandstärke)
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Stehergröße</Label>
                <Select value={gatePostSize} onValueChange={onGatePostSizeChange}>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="ohne Steher" />
                  </SelectTrigger>
                  <SelectContent>
                    {gatePostSizes.map((size) => (
                      <SelectItem key={size.id} value={size.id}>
                        <div className="font-medium">{size.label}</div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {gatePostSize && gatePostSize !== 'none' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Anzahl Torsteher</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={gatePostQuantity}
                    onChange={(e) => onGatePostQuantityChange(e.target.value)}
                    placeholder="z.B. 4"
                    className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GateConfiguration;